import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Box, Card, CardContent, Chip, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import {
  getDeliveryCompanies,
  getCompanyStorePayouts,
  getCompanyStorePayoutHistory,
} from "../../api/adminService";
import { formatCurrency, formatDate } from "./constants";

const VIEW_TABS = [
  { value: "requests", label: "Payout Requests" },
  { value: "awaiting", label: "Sent - Awaiting Confirmation" },
  { value: "history", label: "History" },
];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function DeliveryCompanyStoreFees() {
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [companiesError, setCompaniesError] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [view, setView] = useState("requests");
  const [rows, setRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    setCompaniesLoading(true);
    getDeliveryCompanies()
      .then((data) => {
        setCompanies(data);
        setCompaniesError("");
      })
      .catch(() => setCompaniesError("Could not load delivery companies. Is the API running?"))
      .finally(() => setCompaniesLoading(false));
  }, []);

  function openCompany(company) {
    setSelectedCompany(company);
    setView("requests");
    setDetailLoading(true);
    Promise.all([getCompanyStorePayouts(company.userId), getCompanyStorePayoutHistory(company.userId)])
      .then(([payouts, history]) => {
        setRows(payouts);
        setHistoryRows(history);
        setDetailError("");
      })
      .catch(() => setDetailError("Could not load store fees for this company."))
      .finally(() => setDetailLoading(false));
  }

  const requestRows = useMemo(
    () => rows.filter((r) => r.requestedAmount > 0 || r.pendingAmount > 0),
    [rows]
  );

  // StorePayoutStatus: Sent = 1, Received = 2 (serialized as the numeric enum value)
  const awaitingRows = useMemo(() => historyRows.filter((r) => r.status === 1), [historyRows]);
  const completedRows = useMemo(() => historyRows.filter((r) => r.status === 2), [historyRows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          requested: acc.requested + (r.requestedAmount || 0),
          newPending: acc.newPending + (r.pendingAmount || 0),
          awaitingConfirmation: acc.awaitingConfirmation + (r.awaitingConfirmationAmount || 0),
          totalPaid: acc.totalPaid + (r.totalPaid || 0),
        }),
        { requested: 0, newPending: 0, awaitingConfirmation: 0, totalPaid: 0 }
      ),
    [rows]
  );

  const companyColumns = useMemo(
    () => [
      { field: "companyName", headerName: "Company", flex: 1, minWidth: 200 },
      { field: "driverCount", headerName: "Drivers", width: 100 },
      {
        field: "hasFullAccess",
        headerName: "Access",
        width: 160,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? "Full access" : "Restricted"}
            color={params.value ? "success" : "warning"}
          />
        ),
      },
    ],
    []
  );

  const requestColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "hasOpenRequest",
        headerName: "Requested By Owner",
        width: 190,
        sortable: false,
        renderCell: (params) =>
          params.row.hasOpenRequest ? (
            <Box sx={{ py: 1 }}>
              <Chip size="small" color="warning" label="Requested" />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {formatDate(params.row.requestedAt)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Not requested yet
            </Typography>
          ),
      },
      {
        field: "requestedAmount",
        headerName: "Requested Amount",
        width: 160,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "requestedOrderCount", headerName: "Orders", width: 90 },
      {
        field: "pendingAmount",
        headerName: "New (Not Yet Requested)",
        width: 190,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      { field: "pendingOrderCount", headerName: "Orders", width: 90 },
    ],
    []
  );

  const awaitingColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        width: 190,
        sortable: false,
        renderCell: () => <Chip size="small" color="info" label="Sent - Awaiting Confirmation" />,
      },
      { field: "orderCount", headerName: "Orders", width: 90 },
      { field: "amount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "sentAt", headerName: "Sent At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: "Note", flex: 1, minWidth: 150 },
    ],
    []
  );

  const historyColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        renderCell: () => <Chip size="small" color="success" label="Received" />,
      },
      { field: "orderCount", headerName: "Orders", width: 90 },
      { field: "amount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "sentAt", headerName: "Sent At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "receivedAt", headerName: "Confirmed At", width: 160, valueFormatter: (v) => formatDate(v) },
      { field: "note", headerName: "Note", flex: 1, minWidth: 150 },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Delivery Company Store Fees
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select a delivery company below to see what it currently owes each store for
        cash-on-delivery orders its drivers delivered - the same view the company sees on its own
        Store Fees page.
      </Typography>

      {companiesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {companiesError}
        </Alert>
      )}

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, mb: 4 }}>
        <DataGrid
          autoHeight
          rows={companies}
          getRowId={(row) => row.userId}
          columns={companyColumns}
          loading={companiesLoading}
          disableRowSelectionOnClick
          onRowClick={(params) => openCompany(params.row)}
          sx={{ border: "none" }}
        />
      </Box>

      {selectedCompany && (
        <Box>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {selectedCompany.companyName}
          </Typography>

          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={HourglassTopOutlinedIcon}
                label="Requested - Awaiting Send"
                value={formatCurrency(totals.requested)}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={AddCircleOutlineOutlinedIcon}
                label="New - Not Yet Requested"
                value={formatCurrency(totals.newPending)}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={MarkEmailReadOutlinedIcon}
                label="Sent - Awaiting Confirmation"
                value={formatCurrency(totals.awaitingConfirmation)}
                color="secondary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={PaidOutlinedIcon}
                label="Total Paid All-Time"
                value={formatCurrency(totals.totalPaid)}
                color="success"
              />
            </Grid>
          </Grid>

          <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mb: 2 }}>
            {VIEW_TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>

          {view === "requests" && (
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
              <DataGrid
                autoHeight
                rows={requestRows}
                getRowId={(row) => row.storeId}
                columns={requestColumns}
                loading={detailLoading}
                hideFooter
                disableRowSelectionOnClick
                getRowHeight={() => "auto"}
                sx={{ border: "none" }}
              />
              {!detailLoading && requestRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                  Nothing owed to any store right now.
                </Typography>
              )}
            </Box>
          )}

          {view === "awaiting" && (
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
              <DataGrid
                autoHeight
                rows={awaitingRows}
                getRowId={(row) => row.storePayoutId}
                columns={awaitingColumns}
                loading={detailLoading}
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: "none" }}
              />
              {!detailLoading && awaitingRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                  Nothing sent and awaiting confirmation right now.
                </Typography>
              )}
            </Box>
          )}

          {view === "history" && (
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
              <DataGrid
                autoHeight
                rows={completedRows}
                getRowId={(row) => row.storePayoutId}
                columns={historyColumns}
                loading={detailLoading}
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: "none" }}
              />
              {!detailLoading && completedRows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                  No confirmed payouts yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
